(() => {
  const elementName = 'kanaan-homepage-reviews';

  if (customElements.get(elementName)) return;

  class KanaanHomepageReviews extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.rows = Array.from(this.querySelectorAll('[data-kanaan-homepage-reviews-row]'));
      this.status = this.querySelector('[data-kanaan-homepage-reviews-status]');
      this.autoScrollFrames = [];
      this.pauseRowForMouse = (event) => {
        if (event.pointerType === 'mouse') event.currentTarget.dataset.mouseHover = 'true';
      };
      this.resumeRowForMouse = (event) => {
        if (event.pointerType === 'mouse') delete event.currentTarget.dataset.mouseHover;
      };

      if (this.rows.length === 0 || !this.status || !this.dataset.source) return;

      this.rows.forEach((row) => {
        row.addEventListener('pointerenter', this.pauseRowForMouse);
        row.addEventListener('pointerleave', this.resumeRowForMouse);
      });

      this.loadReviews();
    }

    disconnectedCallback() {
      this.stopAutoScroll();

      if (this.rows) {
        this.rows.forEach((row) => {
          row.removeEventListener('pointerenter', this.pauseRowForMouse);
          row.removeEventListener('pointerleave', this.resumeRowForMouse);
        });
      }

      this.dataset.initialized = '';
    }

    async loadReviews() {
      try {
        const response = await fetch(this.dataset.source, { credentials: 'same-origin' });

        if (!response.ok) throw new Error('Review data could not be loaded.');

        const data = await response.json();
        const reviews = this.removeDuplicateReviews(data);
        if (!this.isConnected) return;
        this.renderReviews(reviews);
        this.startAutoScroll();
        this.status.textContent = 'Showing all ' + reviews.length + ' customer reviews, with no repeated entries.';
      } catch (error) {
        this.status.textContent = 'Customer reviews are temporarily unavailable.';
      }
    }

    removeDuplicateReviews(data) {
      const seen = new Set();

      return Array.isArray(data) ? data.filter((review) => {
        if (!review || !review.name || !review.review || !Number.isInteger(review.rating)) return false;

        const key = [review.name, review.rating, review.review, review.country || ''].join('\u0001');
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
      }) : [];
    }

    renderReviews(reviews) {
      const fragments = this.rows.map(() => document.createDocumentFragment());

      this.rows.forEach((row) => row.replaceChildren());

      reviews.forEach((review, index) => {
        fragments[index % this.rows.length].appendChild(this.createReviewCard(review));
      });

      this.rows.forEach((row, index) => row.appendChild(fragments[index]));
    }

    startAutoScroll() {
      this.stopAutoScroll();

      this.rows.forEach((row, index) => {
        const direction = index % 2 === 0 ? 1 : -1;
        const speed = 56 + index * 8;
        let lastTimestamp = null;
        let position = 0;

        if (direction < 0) {
          position = Math.max(0, row.scrollWidth - row.clientWidth);
          row.scrollLeft = position;
        }

        const animate = (timestamp) => {
          const maxScroll = Math.max(0, row.scrollWidth - row.clientWidth);

          if (lastTimestamp !== null && maxScroll > 0) {
            const elapsed = Math.min(64, timestamp - lastTimestamp);

            if (row.dataset.mouseHover === 'true') {
              position = row.scrollLeft;
            } else {
              // Keep the fractional position outside scrollLeft so low speeds do not round to zero.
              position += direction * speed * (elapsed / 1000);

              if (direction > 0 && position >= maxScroll) position = 0;
              if (direction < 0 && position <= 0) position = maxScroll;

              row.scrollLeft = position;
            }
          }

          lastTimestamp = timestamp;
          this.autoScrollFrames[index] = requestAnimationFrame(animate);
        };

        this.autoScrollFrames[index] = requestAnimationFrame(animate);
      });
    }

    stopAutoScroll() {
      (this.autoScrollFrames || []).forEach((frame) => cancelAnimationFrame(frame));
      this.autoScrollFrames = [];
    }

    createReviewCard(review) {
      const card = document.createElement('article');
      const person = document.createElement('div');
      const initials = document.createElement('span');
      const personCopy = document.createElement('div');
      const name = document.createElement('strong');
      const country = document.createElement('span');
      const quote = document.createElement('p');
      const stars = document.createElement('span');
      const rating = Math.max(1, Math.min(5, review.rating));

      card.className = 'kanaan-homepage-reviews__card';
      person.className = 'kanaan-homepage-reviews__person';
      initials.className = 'kanaan-homepage-reviews__initials';
      name.textContent = review.name;
      country.textContent = review.country || '';
      quote.className = 'kanaan-homepage-reviews__quote';
      quote.dir = 'auto';
      quote.textContent = '“' + review.review + '”';
      stars.className = 'kanaan-homepage-reviews__stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', 'Rated ' + rating + ' out of 5 stars');
      stars.textContent = '★★★★★'.slice(0, rating) + '☆☆☆☆☆'.slice(0, 5 - rating);
      initials.textContent = this.getInitials(review.name);

      personCopy.append(name, country);
      person.append(initials, personCopy);
      card.append(person, quote, stars);
      return card;
    }

    getInitials(name) {
      return name
        .replace(/[.]/g, '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase();
    }
  }

  customElements.define(elementName, KanaanHomepageReviews);
})();
